import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { useLocalStorage } from "@/shared/hooks";
import { cn } from "@/shared/utils/helpers";

import { Sidebar } from "./components/Sidebar";
import { Topbar } from "./components/Topbar";

export function AppShell() {
  // Desktop icon-rail preference persists across sessions.
  const [collapsed, setCollapsed] = useLocalStorage<boolean>(
    "portal:sidebar-collapsed",
    false,
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  // Close the mobile drawer after navigating.
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // Close the drawer when the viewport reaches the desktop breakpoint, where
  // the sidebar is permanently visible (otherwise a stale drawer/backdrop
  // would linger after a resize/rotate). No-op when matchMedia is unavailable.
  useEffect(() => {
    if (!mobileOpen) return;
    const mql = window.matchMedia?.("(min-width: 1024px)");
    if (!mql) return;
    if (mql.matches) {
      setMobileOpen(false);
      return;
    }
    const handleChange = (event: MediaQueryListEvent) => {
      if (event.matches) setMobileOpen(false);
    };
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, [mobileOpen]);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  return (
    <div
      className={cn("portal-app", collapsed && "is-collapsed")}
      data-portal-chrome="app"
    >
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((value) => !value)}
        mobileOpen={mobileOpen}
        onNavigate={() => setMobileOpen(false)}
      />

      {mobileOpen ? (
        <button
          type="button"
          aria-hidden
          tabIndex={-1}
          className="portal-backdrop lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}

      <div className="portal-main">
        <Topbar onOpenMobileNav={() => setMobileOpen(true)} />
        <main className="portal-scroll">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
